import { type FC, useState } from 'react';

import { detailListClass } from '@eneco/custom-components/flows/DetailListItem.css';
import { Layout } from '@eneco/flows/components';
import { Box, Grid, Stack } from '@eneco/sparky';

import {
  SummaryAgreement,
  SummaryBenefitsList,
  SummaryDetailsList,
  SummaryPaymentCard,
  SummaryStepDialogs,
  SummaryStepHeader,
  useExpN522SummaryStep,
} from './SummaryStepShared';
import { SummaryContractNotifications, SummaryStepError } from '../../../../shared/components';
import { SummaryOfferCard } from '../components/SummaryOfferCard_B';

/** Experiment summary step variant C. Uses shared summary logic with offer card in the right column. */
export const SummaryStep: FC = () => {
  const {
    label,
    flowContext,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    isOrderFailed,
    selectedOffer,
    selectedOfferTermsAndConditions,
    form,
    textualData,
    isNavigating,
    isTermsDialogOpen,
    setIsTermsDialogOpen,
    isContractPeriodDialogOpen,
    setIsContractPeriodDialogOpen,
    isConditionsDialogOpen,
    setIsConditionsDialogOpen,
    isTariffsDialogOpen,
    setIsTariffsDialogOpen,
    displayName,
    formattedAddress,
    contractPeriod,
    goToStep,
  } = useExpN522SummaryStep();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitSuccessful },
  } = form;

  const [isPromotionPriceDetailsOpen, setIsPromotionPriceDetailsOpen] = useState(false);
  const { isPersonalDetailsEditProhibited, energyType } = flowContext;

  return (
    <Layout.Content variant="C" isLoading={isLoading || isNavigating} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <SummaryStepHeader />
          <SummaryContractNotifications selectedOffer={selectedOffer} notifications={textualData?.notifications} />

          <Grid
            gridTemplateColumns={{ initial: 'minmax(0, 1fr)', lg: 'minmax(0, 2fr) minmax(0, 1fr)' }}
            gap="6"
            alignY="start">
            <Grid.Item order={{ initial: 1, lg: 1 }}>
              <Box className={detailListClass}>
                <Stack direction="column" gap="3">
                  <Stack.Item>
                    <SummaryDetailsList
                      displayName={displayName}
                      birthdate={flowContext.birthdate}
                      birthDateLabel={textualData?.personalDetailsOverview?.birthDateLabel?.value}
                      street={formattedAddress.street}
                      postalCodeCity={formattedAddress.postalCodeCity}
                      contractPeriod={contractPeriod}
                      emailAddress={flowContext.emailAddress ?? ''}
                      phoneNumber={flowContext.phoneNumber ?? ''}
                      isMoving={flowContext.isMoving}
                      onGoToStep={goToStep}
                      onOpenContractPeriod={() => setIsContractPeriodDialogOpen(true)}
                      onOpenTerms={() => setIsTermsDialogOpen(true)}
                    />
                  </Stack.Item>

                  <Stack.Item>
                    <SummaryPaymentCard
                      textualData={textualData?.paymentDetails}
                      register={register}
                      ibanError={errors.iban}
                      isPersonalDetailsEditProhibited={isPersonalDetailsEditProhibited}
                    />
                  </Stack.Item>
                </Stack>
              </Box>
            </Grid.Item>

            {selectedOffer && (
              <Grid.Item order={{ initial: 2, lg: 2 }}>
                <Box paddingTop={{ initial: 6, lg: 0 }}>
                  <Stack direction="column" gap="3">
                    <SummaryOfferCard
                      offer={selectedOffer}
                      isOpen={isPromotionPriceDetailsOpen}
                      setOpen={setIsPromotionPriceDetailsOpen}
                      textualData={textualData}
                      flowContext={flowContext}
                      energyType={energyType}
                    />
                    <SummaryBenefitsList />
                  </Stack>
                </Box>
              </Grid.Item>
            )}

            <Grid.Item order={{ initial: 3, lg: 3 }}>
              <SummaryAgreement
                nextStepText={textualData?.content?.nextStepText?.value}
                isLoading={isSubmitSuccessful}
                onOpenConditions={() => setIsConditionsDialogOpen(true)}
                onOpenTariffs={() => setIsTariffsDialogOpen(true)}
                textualData={textualData}
              />
            </Grid.Item>
          </Grid>

          <SummaryStepDialogs
            label={label}
            flowContext={flowContext}
            textualData={textualData}
            selectedOffer={selectedOffer}
            selectedOfferTermsAndConditions={selectedOfferTermsAndConditions}
            isTermsDialogOpen={isTermsDialogOpen}
            setIsTermsDialogOpen={setIsTermsDialogOpen}
            isContractPeriodDialogOpen={isContractPeriodDialogOpen}
            setIsContractPeriodDialogOpen={setIsContractPeriodDialogOpen}
            isConditionsDialogOpen={isConditionsDialogOpen}
            setIsConditionsDialogOpen={setIsConditionsDialogOpen}
            isTariffsDialogOpen={isTariffsDialogOpen}
            setIsTariffsDialogOpen={setIsTariffsDialogOpen}
          />
        </>
      )}

      {(isFailed || isOrderFailed) && (
        <SummaryStepError
          isNotAuthenticated={isNotAuthenticated}
          isNotAvailable={isNotAvailable}
          isNotFound={isNotFound}
          isOrderFailed={isOrderFailed}
          textualData={textualData?.genericError}
        />
      )}
    </Layout.Content>
  );
};
